import { Box, LuxTextArea, useMediaQuery } from '@gemini/fe-ds-core';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { FieldScrollTarget } from '@common/FieldScroll';
import { getInsuranceFieldErrorProps } from '../InsurancesForm.utils';
import {
  CustomInsuranceFieldsFormData,
  CustomInsuranceFieldsFormProps,
} from './CustomInsuranceFieldsForm.interface';
import { customInsuranceFieldsFormSchema } from './CustomInsuranceFieldsForm.schema';
import {
  CUSTOM_INSURANCE_FIELD_MAX_LENGTH,
  getVisibleCustomInsuranceFields,
  toCustomInsuranceDefaultValues,
} from './CustomInsuranceFieldsForm.utils';
import useNoopClickableGuard from '@hooks/useNoopClickableGuard';

const CustomInsuranceFieldsForm = ({
  currentIndex,
  customFields,
  initialValues,
  isVisible = true,

  submitAttemptCount = 0,
  onValidate,
  onChange,
  onCommit,
}: CustomInsuranceFieldsFormProps) => {
  const brandName = useOnlineBookingStore(state => state.brandName);
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  const customInsuranceFieldsClickableGuardOptions = useMemo(
    () => ({
      forceSelector: '*',
      hideRedundantSelectLabels: true,
    }),
    [],
  );
  const formRef = useNoopClickableGuard<HTMLDivElement>(
    customInsuranceFieldsClickableGuardOptions,
  );
  const handledSubmitAttemptCountRef = useRef(submitAttemptCount);

  const validationMode = brandName ? 'onBlur' : 'onChange';
  const [showSubmittedErrors, setShowSubmittedErrors] = useState(false);

  const visibleFields = useMemo(
    () => getVisibleCustomInsuranceFields(customFields),
    [customFields],
  );

  const {
    control,
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = useForm<CustomInsuranceFieldsFormData>({
    mode: validationMode,
    reValidateMode: validationMode,
    resolver: yupResolver(
      customInsuranceFieldsFormSchema(customFields),
    ) as Resolver<CustomInsuranceFieldsFormData>,
    defaultValues: toCustomInsuranceDefaultValues(customFields, initialValues),
  });

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    if (!visibleFields.length) {
      onValidate(true, currentIndex);
      return;
    }

    void trigger();
  }, [currentIndex, onValidate, trigger, visibleFields.length]);

  useEffect(() => {
    onValidate(!visibleFields.length || isValid, currentIndex);
  }, [currentIndex, isValid, onValidate, visibleFields.length]);

  useEffect(() => {
    if (submitAttemptCount === 0 || !isVisible) {
      if (submitAttemptCount === 0) {
        handledSubmitAttemptCountRef.current = 0;
      }
      setShowSubmittedErrors(false);

      return;
    }

    if (handledSubmitAttemptCountRef.current === submitAttemptCount) {
      return;
    }

    handledSubmitAttemptCountRef.current = submitAttemptCount;

    void trigger().then(() => {
      setShowSubmittedErrors(true);
    });
  }, [isVisible, submitAttemptCount, trigger]);

  useEffect(() => {
    const subscription = watch(value => {
      onChangeRef.current?.(value as CustomInsuranceFieldsFormData);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    return () => {
      onCommitRef.current?.(getValues());
    };
  }, [getValues]);

  if (!visibleFields.length) {
    return null;
  }

  return (
    <Box
      ref={formRef}
      padding="0.5rem 1rem 1rem 1rem"
      display={isVisible ? 'grid' : 'none'}
      gridTemplateColumns="1fr"
      gap="1rem"
    >
      {visibleFields.map(fieldConfig => (
        <Controller
          key={fieldConfig.fieldKey}
          name={
            `patientDetails.storeCustomFieldsInsurance.${fieldConfig.fieldKey}` as const
          }
          control={control}
          render={({ field, fieldState }) => {
            const fieldErrorProps = getInsuranceFieldErrorProps(
              fieldState,
              validationMode,
              showSubmittedErrors,
            );
            const fieldName = `patientDetails.storeCustomFieldsInsurance.${fieldConfig.fieldKey}`;

            return (
              <FieldScrollTarget
                name={fieldName}
                hasError={fieldErrorProps.error}
                isMobile={isMobile}
              >
                <LuxTextArea
                  {...field}
                  minRows={1}
                  inputProps={{
                    maxLength: CUSTOM_INSURANCE_FIELD_MAX_LENGTH,
                  }}
                  limit={CUSTOM_INSURANCE_FIELD_MAX_LENGTH}
                  resizable
                  fullWidth
                  label={fieldConfig.label}
                  required={fieldConfig.required}
                  value={field.value || ''}
                  {...fieldErrorProps}
                />
              </FieldScrollTarget>
            );
          }}
        />
      ))}
    </Box>
  );
};

export default memo(CustomInsuranceFieldsForm);
